import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AppShell } from "@/components/AppShell";
import { getInvite, joinInvite } from "@/api";
import { useAuth } from "@/context/AuthContext";

export default function InvitePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadInvite = async () => {
      try {
        const { data } = await getInvite(code);
        setInvite(data);
      } catch (error) {
        if (error?.response?.status === 410) {
          setStatus("This invite has expired or been used.");
        } else {
          setStatus("This invite has faded or no longer exists.");
        }
      } finally {
        setLoading(false);
      }
    };
    loadInvite();
  }, [code]);

  const handleJoin = async () => {
    setStatus("");
    if (!user) {
      navigate(`/auth?return=/invite/${code}`);
      return;
    }
    try {
      const { data } = await joinInvite(code);
      navigate(`/rooms/${data.id}`);
    } catch (error) {
      if (error?.response?.status === 401) {
        navigate(`/auth?return=/invite/${code}`);
        return;
      }
      if (error?.response?.status === 410) {
        setStatus("This invite has expired or been used.");
        return;
      }
      setStatus("Unable to join this room.");
    }
  };

  return (
    <AppShell>
      <section className="space-y-6" data-testid="invite-page">
        <div data-testid="invite-header">
          <p className="text-xs uppercase tracking-[0.4em] text-stone-500" data-testid="invite-eyebrow">
            Invite
          </p>
          <h1 className="font-cinzel text-3xl" data-testid="invite-title">
            Accept the War Room summons
          </h1>
          <p className="text-stone-400" data-testid="invite-description">
            Join your party using the Dracon Gaming invite link.
          </p>
        </div>

        {loading ? (
          <div className="text-stone-400" data-testid="invite-loading">
            Reading the sigil...
          </div>
        ) : invite ? (
          <div className="panel-iron rounded-xl p-8 space-y-4 max-w-xl" data-testid="invite-card">
            <h2 className="font-cinzel text-2xl" data-testid="invite-room-name">
              {invite.room_name}
            </h2>
            <p className="text-stone-400" data-testid="invite-room-description">
              {invite.room_description || "A realm without recorded lore."}
            </p>
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500" data-testid="invite-expiry">
              Expires {new Date(invite.expires_at).toLocaleString()} · {invite.remaining_uses} use(s) remaining
            </p>
            <Button
              type="button"
              className="bg-red-900 hover:bg-red-800 text-stone-100 font-cinzel uppercase tracking-[0.25em]"
              onClick={handleJoin}
              data-testid="invite-join-button"
            >
              Join Room
            </Button>
          </div>
        ) : (
          <div className="panel-iron rounded-xl p-6" data-testid="invite-error">
            {status}
          </div>
        )}

        {status && invite && (
          <div className="text-red-200" data-testid="invite-status">
            {status}
          </div>
        )}
      </section>
    </AppShell>
  );
}
